import type { DateGranularity, ReportConfig } from "../types";

const REPORTING_PERIOD_OPTION_DEFS = [
    { value: "PreviousMonth", name: "Previous Month" },
    { value: "CurrentMonthToDate", name: "Current Month to Date" },
    { value: "Last7Days", name: "Last 7 Days" },
    { value: "Last30Days", name: "Last 30 Days" },
    { value: "CurrentQuarter", name: "Current Quarter" },
    { value: "PreviousQuarter", name: "Previous Quarter" },
    { value: "YearToDate", name: "Year to Date" },
    { value: "CustomRange", name: "Custom Range" },
    { value: "LastNDays", name: "Last N Days" },
    { value: "LastNMonths", name: "Last N Complete Months" },
];

// "Last N Days" / "Last N Complete Months": N lives on reportingPeriod.lastN. Mirrors
// ReportSqlBuilder.LastNSql / MaxLastNDays / MaxLastNMonths - keep the limits in step.
const LAST_N_LIMITS = { LastNDays: 3660, LastNMonths: 120 } as const;
const LAST_N_DEFAULTS = { LastNDays: 30, LastNMonths: 3 } as const;
const LAST_N_PERIOD_TYPES = Object.keys(LAST_N_LIMITS);
const formatReportDate = (value?: string) => {
    if (!value) return "";
    if (value.toLowerCase() === "ongoing") return "Ongoing";

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return value;

    return `${match[2]}/${match[3]}/${match[1]}`;
};

function normalizeReportingPeriodType(type: string) {
    const normalized = String(type || "").trim().toLowerCase();
    const match = REPORTING_PERIOD_OPTION_DEFS.find((option) =>
        option.value.toLowerCase() === normalized || option.name.toLowerCase() === normalized);
    return match?.value || type;
}

function reportingPeriodName(type: string) {
    const normalized = normalizeReportingPeriodType(type);
    return REPORTING_PERIOD_OPTION_DEFS.find((option) => option.value === normalized)?.name || type;
}

function isCustomReportingPeriod(type: string) {
    return normalizeReportingPeriodType(type) === "CustomRange";
}

function isLastNReportingPeriod(type: string): type is "LastNDays" | "LastNMonths" {
    return LAST_N_PERIOD_TYPES.includes(normalizeReportingPeriodType(type));
}

function lastNLimit(type: string) {
    const normalized = normalizeReportingPeriodType(type);
    return normalized === "LastNMonths" ? LAST_N_LIMITS.LastNMonths : LAST_N_LIMITS.LastNDays;
}

function lastNDefault(type: string) {
    return normalizeReportingPeriodType(type) === "LastNMonths" ? LAST_N_DEFAULTS.LastNMonths : LAST_N_DEFAULTS.LastNDays;
}

/** A usable N (0 = today / the current month to date), or null when it is missing, not a whole number, or out of range. */
function validLastN(type: string, value: unknown): number | null {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isInteger(n) && n >= 0 && n <= lastNLimit(type) ? n : null;
}

/**
 * The period picker's options: the lookup values when loaded, with any built-in period the
 * lookup table does not have yet (e.g. Last N Days before its row is seeded) appended so it
 * can still be chosen. With no lookup values at all, the built-in list.
 */
function withBuiltInReportingPeriods<T extends { id?: string | number; value: string; name: string }>(options: T[]) {
    const builtIns = REPORTING_PERIOD_OPTION_DEFS.map((option) => ({ id: option.value, value: option.value, name: option.name }));
    if (!options.length) return builtIns;
    const known = new Set(options.map((option) => normalizeReportingPeriodType(option.value)));
    return [...options, ...builtIns.filter((option) => LAST_N_PERIOD_TYPES.includes(option.value) && !known.has(option.value))];
}

const DATE_GRANULARITY_OPTIONS: { key: DateGranularity; label: string }[] = [
    { key: "year", label: "Year" },
    { key: "quarter", label: "Quarter" },
    { key: "month", label: "Month" },
    { key: "week", label: "Week" },
    { key: "day", label: "Day" },
];

// Builds a GROUP BY-safe expression that extracts a date part from a timestamp column, for
// extra breakdowns like "Year" / "Month" on top of an existing count or percentage metric.
function dateGranularityExpr(columnSql: string, granularity: DateGranularity) {
    const sourceSql = columnSql.includes("\n") ? `(${columnSql})` : columnSql;
    switch (granularity) {
        case "year": return { select: `YEAR(${sourceSql})`, groupBy: `YEAR(${sourceSql})` };
        case "quarter": return { select: `DATEPART(QUARTER, ${sourceSql})`, groupBy: `DATEPART(QUARTER, ${sourceSql})` };
        // First 3 letters of the month name ("Jan", "Feb", ...), matching the Excel column
        // headers ReportPivotBuilder writes (MonthAbbrev), which order and fill these
        // chronologically. Mirrors ReportSqlBuilder.DateGranularityExpr.
        case "month": return { select: `LEFT(DATENAME(MONTH, ${sourceSql}), 3)`, groupBy: `LEFT(DATENAME(MONTH, ${sourceSql}), 3)` };
        case "week": return { select: `DATEPART(WEEK, ${sourceSql})`, groupBy: `DATEPART(WEEK, ${sourceSql})` };
        case "day": return { select: `CAST(${sourceSql} AS DATE)`, groupBy: `CAST(${sourceSql} AS DATE)` };
        default: return { select: sourceSql, groupBy: sourceSql };
    }
}

function granularityLabel(granularity?: DateGranularity) {
    return granularity ? DATE_GRANULARITY_OPTIONS.find((option) => option.key === granularity)?.label || granularity : null;
}

function periodPlainEnglish(report: ReportConfig) {
    if (isLastNReportingPeriod(report.reportingPeriod.type)) {
        const n = validLastN(report.reportingPeriod.type, report.reportingPeriod.lastN);
        const months = normalizeReportingPeriodType(report.reportingPeriod.type) === "LastNMonths";
        const unit = months ? "complete month" : "day";
        if (n === 0) return months ? "the current month to date" : "today";
        return n !== null ? `last ${n} ${unit}${n === 1 ? "" : "s"}` : `last N ${unit}s (N not set)`;
    }
    if (isCustomReportingPeriod(report.reportingPeriod.type)) {
        return `between ${formatReportDate(report.reportingPeriod.customStart) || "(start not set)"} and ${formatReportDate(report.reportingPeriod.customEnd) || "(end not set)"}`;
    }
    return reportingPeriodName(report.reportingPeriod.type).toLowerCase();
}

export {
    REPORTING_PERIOD_OPTION_DEFS,
    formatReportDate,
    normalizeReportingPeriodType,
    reportingPeriodName,
    isCustomReportingPeriod,
    isLastNReportingPeriod,
    lastNLimit,
    lastNDefault,
    validLastN,
    withBuiltInReportingPeriods,
    DATE_GRANULARITY_OPTIONS,
    dateGranularityExpr,
    granularityLabel,
    periodPlainEnglish,
};
